import {
  EContractStatus,
  ECreditApplicationStatus,
  ECreditApplicationStep,
  EDecision,
  EDocumentStatus,
  EDocumentType,
  EOriginSystemId,
  EWorksheetStatus,
} from '@if/core/types'
import { CircularProgress, Typography } from '@mui/material'
import {
  areAllRequiredDocumentForStepApproved,
  availableCreditEqualApprovedAmount,
  isContractExpired,
} from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useFeatureFlags from '@src/hooks/config-hooks'
import StepStatusSection from '../../../App/components/StepStatusSection'
import { EIconType } from '../../../App/components/iconSection'
import { ECreditApplicationStatusMessage, GetContractIconAndMessage } from '../../viewCreditApp-selectors'
import { canSendOrDeleteHighEstimateInvoice } from '../DocumentSection/documentTableRow-hooks'
import ContractAwaitingSignature from './ContractAwaitingSignature'
import ContractCompleted from './ContractCompleted'
import CreateContractButton from './CreateContractButton'

interface Props {
  creditApp: CreditApplication
  isPolling: boolean
  canEdit: boolean
}

const ContractTile = ({ creditApp, isPolling, canEdit }: Props) => {
  const { t } = useTranslation()
  const { flags } = useFeatureFlags()
  const isExpiryFeatureActive =
    !!flags.UseMerchDBContractUpdateOnExpiry &&
    FinancingProgramConfigs[creditApp.financingProgramId]?.supportsContractUpdateOnExpiry === true
  const expired = isContractExpired(creditApp, isExpiryFeatureActive)
  const rawIcon = GetContractIconAndMessage(creditApp, creditApp.worksheet)
  const icon = expired
    ? { iconMessage: ECreditApplicationStatusMessage.Expired, iconType: EIconType.error }
    : rawIcon

  // Contract validation logic
  const currentConfig = FinancingProgramConfigs[creditApp.financingProgramId]
  const canCreateContract =
    !currentConfig?.mustProvideWarrantyContract ||
    !!creditApp.requiredDocuments.find(
      (doc) => doc.typeId === EDocumentType.ExtendedWarrantyContract && doc.status !== EDocumentStatus.AwaitingDocument,
    )

  const allDocumentsRequiredForCvtApproved =
    areAllRequiredDocumentForStepApproved(creditApp, ECreditApplicationStep.CVT) &&
    areAllRequiredDocumentForStepApproved(creditApp, ECreditApplicationStep.Financing)
  const isWorksheetApproved = creditApp.worksheet?.finalDecision.decision === EDecision.Approved
  const contractTileTitle =
    isWorksheetApproved &&
    (creditApp.contract.status === EContractStatus.Completed ||
      creditApp.contract.status === EContractStatus.Ready ||
      creditApp.contract.status === EContractStatus.Sending)
      ? `${t('worksheet.contractNumber')} #${creditApp.contract.cvtNumber}`
      : t('viewCreditApplication.contractSectionTitle')

  const highEstimateinvoiceDelayExpired = useMemo(() => {
    if (
      creditApp.worksheet?.isHighEstimate &&
      creditApp.worksheet.firstPaymentOn &&
      canEdit &&
      creditApp.requiredDocuments.find((x) => x.typeId === EDocumentType.ClinicInvoice) &&
      !canSendOrDeleteHighEstimateInvoice(new Date(creditApp.worksheet.firstPaymentOn))
    ) {
      return true
    }
    return false
  }, [canEdit, creditApp])

  if (creditApp.finalCreditDecision.decision !== EDecision.Approved) {
    return (
      <StepStatusSection title={t('viewCreditApplication.contractSectionTitle')}>
        <Typography variant="body1">{t('viewCreditApplication.creditNotApproved')}</Typography>{' '}
      </StepStatusSection>
    )
  }

  if (
    FinancingProgramConfigs[creditApp.financingProgramId].worksheetIsNeededForCredit &&
    creditApp.worksheet === null &&
    creditApp.consentHardHit === true &&
    creditApp.originSystemId === EOriginSystemId.Subscription
  ) {
    return (
      <StepStatusSection title={t('viewCreditApplication.contractSectionTitle')}>
        <Typography>{t('viewCreditApplication.creditNotApproved')}</Typography>
      </StepStatusSection>
    )
  }

  if (!creditApp.allIncomesConfirmed) {
    // Income Not Completed
    return (
      <StepStatusSection title={t('viewCreditApplication.contractSectionTitle')}>
        <Typography variant="body1">{t('viewCreditApplication.incomeNotCompleted')}</Typography>{' '}
      </StepStatusSection>
    )
  }

  if (creditApp.hasTasksBeforeCvt) {
    // Cvt Tasks Not Completed
    return (
      <StepStatusSection
        title={t('viewCreditApplication.contractSectionTitle')}
        iconMessage={icon.iconMessage ? t(`creditApplicationStatus.${icon.iconMessage}`) : null}
        iconType={icon.iconType}
      >
        <Typography variant="body1">{t('viewCreditApplication.cvtTasksNotCompleted')}</Typography>
      </StepStatusSection>
    )
  }

  if (!allDocumentsRequiredForCvtApproved) {
    return (
      <StepStatusSection
        title={contractTileTitle}
        iconMessage={icon.iconMessage ? t(`creditApplicationStatus.${icon.iconMessage}`) : null}
        iconType={icon.iconType}
      >
        <>
          {icon.iconMessage === ECreditApplicationStatusMessage.DocumentsRequired && (
            <Typography variant="body1">{t('viewCreditApplication.requiredDocuments')}</Typography>
          )}
          {icon.iconMessage === ECreditApplicationStatusMessage.AwaitingApproval && (
            <Typography variant="body1">{t('viewCreditApplication.documentsAwaitingApproval')}</Typography>
          )}
        </>
      </StepStatusSection>
    )
  }

  const isAvailableCreditEqualApprovaleAmount = availableCreditEqualApprovedAmount(creditApp)

  const showCreateComponent =
    (creditApp.worksheet == null ||
      creditApp.worksheet.status === EWorksheetStatus.Draft ||
      creditApp.worksheet.finalDecision.decision === EDecision.ManualRevision ||
      highEstimateinvoiceDelayExpired) &&
    canEdit &&
    canCreateContract
  return (
    <StepStatusSection
      title={contractTileTitle}
      iconMessage={icon.iconMessage ? t(`creditApplicationStatus.${icon.iconMessage}`) : null}
      iconType={icon.iconType}
    >
      {icon.iconMessage === ECreditApplicationStatusMessage.Cancelled && (
        <Typography variant="body1">{t('viewCreditApplication.creditAppCancelled')}</Typography>
      )}

      {!isAvailableCreditEqualApprovaleAmount && showCreateComponent && (
        <Typography variant="body1">{t('viewCreditApplication.availableCreditNotCorresponding')}</Typography>
      )}

      {!canCreateContract && (
        <Typography variant="body1">{t('viewCreditApplication.extendedWarrantyContractRequired')}</Typography>
      )}

      {!showCreateComponent &&
        (creditApp.worksheet == null || !isWorksheetApproved) &&
        creditApp.status !== ECreditApplicationStatus.Cancelled && (
          <Typography>{t('worksheet.contractNotCreated')}</Typography>
        )}

      {isAvailableCreditEqualApprovaleAmount &&
        showCreateComponent &&
        creditApp.status !== ECreditApplicationStatus.Cancelled && <CreateContractButton creditApp={creditApp} />}

      {isWorksheetApproved && creditApp.contract.status === EContractStatus.Completed && (
        <ContractCompleted creditApp={creditApp} />
      )}
      {isWorksheetApproved &&
        (expired ||
          creditApp.contract.status === EContractStatus.Ready ||
          creditApp.contract.status === EContractStatus.Sending ||
          creditApp.contract.status === EContractStatus.UnderReview) && (
          <ContractAwaitingSignature
            creditApp={creditApp}
            isExpiryFeatureActive={isExpiryFeatureActive}
            isExpired={expired}
          />
        )}

      {isWorksheetApproved && !expired && creditApp.contract.status === EContractStatus.NotReady && isPolling && (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}
      {isWorksheetApproved && !expired && creditApp.contract.status === EContractStatus.NotReady && !isPolling && (
        <div>
          <Typography> {t('worksheet.error')}</Typography>
        </div>
      )}
    </StepStatusSection>
  )
}

export default ContractTile
